// src\components\AiDashboard\AiMyNutritionStatus\AiUser\AiUserInputSeparator\AiUserInputSeparator.js
// Returns: { accepted, reason, timestampIso, categories, minimal }

import mealCategorizer from './AiMealCategorizer';

class AiUserInputSeparator {
  constructor(config = {}) {
    this.serviceName = config.serviceName || 'AiUserInputSeparator';
    this.maxHopCount = Number(config.maxHopCount || 10);
    this.replayWindowMillis = Number(config.replayWindowMillis || 10 * 60 * 1000);

    this.allowedRoutes = Object.freeze({
      user_input_ingress: Object.freeze(['separator_ingress']),
    });

    // In-memory stores
    this.rawEventStore = [];
    this.separatedHistory = [];
    this.parsedBuffer = [];
    this.categorizedBuffer = [];
    this.deadLetterQueue = [];

    // Idempotency / change detection
    this.idempotencyRegistry = new Map();
    this.lastChangeHashByEntity = new Map();

    // Per-entity serialization
    this.entityLocks = new Map();
  }

  /**
   * @returns {Promise<{accepted:boolean, reason:null|string, timestampIso:string, categories:object, minimal?:object}>}
   */
  async ingestUserText({
    text,
    entityId,
    sourceServiceName,
    ingress,
    egress,
    requestId,
    traceId,
    idempotencyKey,
  }) {
    const payloadText = String(text ?? '').trim();
    if (!payloadText) {
      return {
        accepted: false,
        reason: 'empty_text',
        timestampIso: this._utcMidnightIso(new Date()),
        categories: { mains: [], sides: [], drinks: [], unknown: [] }
      };
    }

    const safeEntityId = String(entityId || 'user_free_text');
    const nowIso = new Date().toISOString();

    const derivedIdempotencyKey =
      idempotencyKey || await this._deriveIdempotencyKey({ text: payloadText, entityId: safeEntityId });
    if (!this._tryStartIdempotent(derivedIdempotencyKey)) {
      return {
        accepted: false,
        reason: 'duplicate_idempotency',
        timestampIso: this._utcMidnightIso(new Date()),
        categories: { mains: [], sides: [], drinks: [], unknown: [] }
      };
    }

    const changeHash = await this._computeChangeHash({ text: payloadText, entityId: safeEntityId });
    const lastHash = this.lastChangeHashByEntity.get(safeEntityId);
    if (lastHash && lastHash === changeHash) {
      this._markComplete(derivedIdempotencyKey);
      return {
        accepted: false,
        reason: 'no_op_change',
        timestampIso: this._utcMidnightIso(new Date()),
        categories: { mains: [], sides: [], drinks: [], unknown: [] }
      };
    }

    const eventBase = {
      id: this._generateUlid(),
      type: 'user_text',
      source: String(sourceServiceName || 'AiUserInput'),
      ingress: String(ingress || 'user_input_ingress'),
      egress: String(egress || 'separator_ingress'),
      requestId: requestId || this._generateUuid(),
      traceId: traceId || this._generateUuid(),
      visited: [],
      hopCount: 0,
      publishedAt: nowIso,
      idempotencyKey: derivedIdempotencyKey,
      changeHash,
      entityId: safeEntityId,
      payload: { text: payloadText },
    };

    const pre = this._preflight(eventBase);
    if (!pre.ok) {
      this._toDlq({ ...eventBase, dlqReason: pre.reason });
      this._markComplete(derivedIdempotencyKey);
      return {
        accepted: false,
        reason: pre.reason,
        timestampIso: this._utcMidnightIso(new Date()),
        categories: { mains: [], sides: [], drinks: [], unknown: [] }
      };
    }

    // serialize per entity
    const tail = this.entityLocks.get(safeEntityId) || Promise.resolve();
    const nextTail = tail
      .then(() => this._publishWithRetry(eventBase))
      .finally(() => {
        const curr = this.entityLocks.get(safeEntityId);
        if (curr === nextTail) this.entityLocks.delete(safeEntityId);
      });
    this.entityLocks.set(safeEntityId, nextTail);

    const result = await nextTail;
    this._markComplete(derivedIdempotencyKey);

    if (!result.ok) {
      return {
        accepted: false,
        reason: result.reason || 'unknown',
        timestampIso: this._utcMidnightIso(new Date()),
        categories: { mains: [], sides: [], drinks: [], unknown: [] }
      };
    }

    this.lastChangeHashByEntity.set(safeEntityId, changeHash);

    // Minimal snapshot for internal history (for debugging/preview)
    const minimal = this._toMinimalSnapshot({
      text: result.event.payload.text,
      parts: result.parts,
      publishedAt: result.event.publishedAt,
    });
    this.parsedBuffer = [...this.parsedBuffer, minimal];

    // Canonical categorization output (deterministic)
    const cat = mealCategorizer.categorize(result.event.payload.text, { timestampStrategy: 'utc_midnight' });

    // Buffers (no numeric features anymore)
    this.categorizedBuffer = [
      ...this.categorizedBuffer,
      {
        timestampIso: cat.timestampIso,
        request: { text: result.event.payload.text, entityId: safeEntityId },
        categories: cat.categories,
      }
    ];

    // Return categories + minimal JSON so callers can send that to AI
    return {
      accepted: cat.accepted,
      reason: cat.reason,
      timestampIso: cat.timestampIso,
      categories: cat.categories,
      minimal
    };
  }

  // ---- Read models (optional) ----
  getParsedBuffer() { return [...this.parsedBuffer]; }
  getLatestMinimal() {
    return this.parsedBuffer.length ? this.parsedBuffer[this.parsedBuffer.length - 1] : null;
  }
  getCategorizedBuffer() { return [...this.categorizedBuffer]; }
  getLatestCategories() {
    return this.categorizedBuffer.length ? this.categorizedBuffer[this.categorizedBuffer.length - 1] : null;
  }
  getSeparatedHistory() { return [...this.separatedHistory]; }
  getRawEvents() { return [...this.rawEventStore]; }
  getDeadLetterQueue() { return [...this.deadLetterQueue]; }

  // ---- Internals ----
  async _publishWithRetry(event) {
    const attempts = [0, 1, 2];
    const tryOnce = async (i) => {
      const backoff = Math.min(2000, 100 * Math.pow(2, i)) + Math.floor(Math.random() * 100);
      if (i > 0) await this._sleep(backoff);
      try {
        const r = await this._publishOne(event);
        return r.ok ? r : { ok: false, reason: r.reason };
      } catch {
        return { ok: false, reason: 'unexpected_error' };
      }
    };
    let res = await tryOnce(attempts[0]); if (res.ok) return res;
    res = await tryOnce(attempts[1]);     if (res.ok) return res;
    res = await tryOnce(attempts[2]);     if (res.ok) return res;
    this._toDlq({ ...event, dlqReason: res.reason || 'exhausted' });
    return { ok: false, reason: res.reason || 'exhausted' };
  }

  async _publishOne(event) {
    if (event.source === this.serviceName) return { ok: false, reason: 'echo_source_drop' };
    if (Array.isArray(event.visited) && event.visited.includes(this.serviceName)) return { ok: false, reason: 'visited_drop' };
    const allowedEgress = this.allowedRoutes[event.ingress] || [];
    if (!allowedEgress.includes(event.egress)) return { ok: false, reason: 'egress_not_allowed' };
    if (event.ingress === event.egress) return { ok: false, reason: 'same_ingress_blocked' };
    if (event.hopCount >= this.maxHopCount) return { ok: false, reason: 'hop_count_exceeded' };

    const enriched = {
      ...event,
      visited: [...(event.visited || []), this.serviceName],
      hopCount: Number(event.hopCount || 0) + 1,
    };

    const parts = this._separateIntoParts(enriched.payload.text);
    const stored = this._store(enriched, parts);
    if (!stored) return { ok: false, reason: 'store_failed' };
    return { ok: true, parts, event: enriched };
  }

  _separateIntoParts(text) {
    const raw = String(text || '');
    const primarySplit = raw
      .split(/\r?\n|,/)                 // newlines, commas
      .flatMap(s => s.split(/;/))       // semicolons
      .flatMap(s => s.split(/\sand\s/i))// " and "
      .flatMap(s => s.split(/\./))      // periods
      .map(s => s.trim())
      .filter(Boolean);
    const seen = new Set();
    return primarySplit.filter(item => {
      const key = item.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  _store(event, parts) {
    try {
      this.rawEventStore = [...this.rawEventStore, event];
      const record = { text: event.payload.text, parts, entityId: event.entityId, publishedAt: event.publishedAt };
      this.separatedHistory = [...this.separatedHistory, record];
      return true;
    } catch {
      return false;
    }
  }

  _toMinimalSnapshot({ text, parts, publishedAt }) {
    const safeParts = Array.isArray(parts) ? parts : [];
    return {
      timestampIso: String(publishedAt || new Date().toISOString()),
      request: { text: String(text || '') },
      parsePreview: { parts: safeParts, count: safeParts.length },
    };
  }

  _preflight(event) {
    if (!event || event.type !== 'user_text') return { ok: false, reason: 'unsupported_type' };
    if (!event.entityId) return { ok: false, reason: 'missing_entity' };
    if (!event.changeHash) return { ok: false, reason: 'missing_change_hash' };
    if (!event.idempotencyKey) return { ok: false, reason: 'missing_idempotency_key' };
    if (!this.allowedRoutes[event.ingress]) return { ok: false, reason: 'ingress_not_allowed' };
    return { ok: true };
  }

  // ---- Idempotency / hashes / ids ----
  _tryStartIdempotent(key) {
    this._sweepIdempotency();
    const rec = this.idempotencyRegistry.get(key);
    if (rec && rec.complete === false) return false;
    if (rec && rec.complete === true) return false;
    this.idempotencyRegistry.set(key, { startedAt: Date.now(), complete: false });
    return true;
  }
  _markComplete(key) {
    const rec = this.idempotencyRegistry.get(key);
    if (rec) this.idempotencyRegistry.set(key, { ...rec, complete: true });
  }
  _sweepIdempotency() {
    const cutoff = Date.now() - this.replayWindowMillis;
    const survivors = Array.from(this.idempotencyRegistry.entries())
      .filter(([, v]) => (v?.startedAt ?? 0) >= cutoff);
    this.idempotencyRegistry = new Map(survivors);
  }
  async _deriveIdempotencyKey({ text, entityId }) {
    const material = JSON.stringify({ t: String(text || ''), e: String(entityId || '') });
    return (await this._hashSha256(material)) || this._fallbackHash(material);
  }
  async _computeChangeHash({ text, entityId }) {
    const material = JSON.stringify({ v: String(text || ''), e: String(entityId || '') });
    return (await this._hashSha256(material)) || this._fallbackHash(material);
  }
  async _hashSha256(input) {
    try {
      if (typeof crypto !== 'undefined' && crypto.subtle) {
        const enc = new TextEncoder().encode(input);
        const buf = await crypto.subtle.digest('SHA-256', enc);
        const bytes = Array.from(new Uint8Array(buf));
        return btoa(String.fromCharCode(...bytes));
      }
      return null;
    } catch {
      return null;
    }
  }
  _fallbackHash(input) {
    let h = 0x811c9dc5;
    for (let i = 0; i < input.length; i++) {
      h ^= input.charCodeAt(i);
      h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
    }
    return `fh_${h.toString(36)}`;
  }
  _generateUuid() {
    const c = typeof crypto !== 'undefined' && crypto.getRandomValues ? crypto.getRandomValues(new Uint8Array(16)) : null;
    if (c) {
      c[6] = (c[6] & 0x0f) | 0x40;
      c[8] = (c[8] & 0x3f) | 0x80;
      const hex = [...c].map(b => b.toString(16).padStart(2, '0'));
      return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6)}-${hex.slice(6, 8)}-${hex.slice(8, 10)}-${hex.slice(10, 16)}`;
    }
    const rnd = () => Math.floor(Math.random() * 0xffffffff).toString(16).padStart(8, '0');
    return `${rnd().slice(0, 8)}-${rnd().slice(0, 4)}-4${rnd().slice(0, 3)}-a${rnd().slice(0, 3)}-${rnd().slice(0, 12)}`;
  }
  _generateUlid() {
    const now = Date.now().toString(36).padStart(9, '0');
    const rand = (typeof crypto !== 'undefined' && crypto.getRandomValues)
      ? Array.from(crypto.getRandomValues(new Uint8Array(10))).map(b => (b % 36).toString(36)).join('')
      : Math.random().toString(36).slice(2, 12).padEnd(10, '0');
    return `${now}${rand}`.slice(0, 19);
  }
  _utcMidnightIso(d) {
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0)).toISOString();
  }
  _sleep(ms) { return new Promise(res => setTimeout(res, ms)); }
}

// Singleton
const singleton = new AiUserInputSeparator({ serviceName: 'AiUserInputSeparator' });
export default singleton;
